import { type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";
import { colors } from "@/ui/tokens";
import {
  badgeVariants,
  iconColorByVariant,
  presetToVariant,
  type BadgePreset,
  type BadgeVariant,
} from "./Badge.variants";

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
  VariantProps<typeof badgeVariants> {
  preset?: BadgePreset;
  icon?: React.ReactNode;
  iconPosition?: "start" | "end";
}

// Re-export badgeVariants for use in other components
export { badgeVariants };

export function Badge({
  className,
  variant,
  preset,
  size,
  icon,
  iconPosition = "start",
  children,
  ...props
}: BadgeProps) {
  const resolvedVariant: BadgeVariant =
    variant ?? (preset ? presetToVariant[preset] : undefined) ?? "neutral";

  const iconColor = iconColorByVariant[resolvedVariant] ?? colors.text.primary;
  const iconElement =
    icon && React.isValidElement(icon)
      ? React.cloneElement(icon as React.ReactElement<any>, {
        className: cn(
          iconColor,
          size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
          (icon.props as any).className
        ),
        "aria-hidden": true,
      })
      : icon;

  // When icon prop is provided, use structured layout
  // When no icon prop, render children directly to preserve flex layout
  const hasIconProp = icon !== undefined;

  return (
    <span
      className={cn(
        badgeVariants({ variant: resolvedVariant, size }),
        className
      )}
      data-variant={resolvedVariant}
      {...props}
    >
      {hasIconProp ? (
        <>
          {iconPosition === "start" && (
            <span className="flex-shrink-0 leading-none" aria-hidden="true">
              {iconElement}
            </span>
          )}
          <span className="leading-none">{children}</span>
          {iconPosition === "end" && (
            <span className="flex-shrink-0 leading-none" aria-hidden="true">
              {iconElement}
            </span>
          )}
        </>
      ) : (
        // No icon prop: render children directly to preserve flex gap
        children
      )}
    </span>
  );
}

